// Bảng giá / bảng chiết khấu khi thêm đơn vị (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.2): Có sẵn · Tạo mới · Gán sau.
//  · Có sẵn: chọn trong thư viện. Tạo mới: bấm là mở hộp lập bảng của trang Bảng giá / Chiết khấu (người gọi mở) —
//    lưu xong người gọi đặt { mode: 'pick', id } nên bảng mới hiện sẵn trong danh sách. Gán sau: một dòng hậu quả.
//  · Không bao giờ mặc định Gán sau — `defaultSetup` chọn Có sẵn (thư viện có bảng) hoặc Tạo mới.
//  · Dùng chung cho form Mạng lưới (Đại lý / CTV) và hộp tạo đơn vị của khu NPP.

import { AlertTriangle, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Segmented } from '@/components/ui/segmented'

export type SetupMode = 'pick' | 'new' | 'later'

export interface SetupChoice {
  mode: SetupMode
  /** Bảng đang chọn (mode 'pick') */
  id: string
}

export interface SetupOption {
  id: string
  label: string
}

export function defaultSetup(hasOptions: boolean, canCreate: boolean): SetupChoice {
  return { mode: hasOptions || !canCreate ? 'pick' : 'new', id: '' }
}

/** Giá trị gửi máy chủ: id bảng, null = gán sau. `error` khi chưa chọn xong. */
export function setupValue(c: SetupChoice, what: string): { id: string | null; error: string | null } {
  if (c.mode === 'later') return { id: null, error: null }
  if (c.mode === 'new') return { id: null, error: `Tạo ${what} hoặc chọn Gán sau` }
  return c.id ? { id: c.id, error: null } : { id: null, error: `Chọn ${what}` }
}

export function SetupPicker({
  label,
  what,
  value,
  onChange,
  options,
  canCreate,
  onCreate,
  laterWarning,
  error,
  disabled,
  className,
}: {
  /** "Bảng giá" / "Bảng chiết khấu" */
  label: string
  /** "bảng giá" / "bảng chiết khấu" — cho nút Tạo + câu báo lỗi */
  what: string
  value: SetupChoice
  onChange: (next: SetupChoice) => void
  options: SetupOption[]
  /** Có quyền lập bảng mới (không có thì ẩn Tạo mới) */
  canCreate: boolean
  /** Mở hộp lập bảng */
  onCreate: () => void
  laterWarning: string
  error?: string | null
  disabled?: boolean
  className?: string
}) {
  const modes: Array<readonly [SetupMode, string]> = [['pick', 'Có sẵn'], ...(canCreate ? [['new', 'Tạo mới'] as const] : []), ['later', 'Gán sau']]
  const pick = (m: SetupMode) => {
    if (disabled) return
    onChange({ ...value, mode: m })
    // Bấm Tạo mới là mở hộp ngay; đóng hộp không lưu thì vẫn ở Tạo mới, bấm nút bên dưới để mở lại
    if (m === 'new') onCreate()
  }
  return (
    <fieldset className={cn('min-w-0 space-y-2', className)} disabled={disabled}>
      <legend className="mb-1.5 text-sm text-inkSoft">{label}</legend>
      <Segmented value={value.mode} onChange={pick} options={modes} label={label} className="text-sm" />
      {value.mode === 'pick' &&
        (options.length === 0 ? (
          <p className="text-sm text-muted">Chưa có {what} nào.</p>
        ) : (
          <select
            aria-label={label}
            className={cn('h-11 w-full rounded-md border bg-paper px-2 text-sm desktop:h-9', error ? 'border-danger' : 'border-rule')}
            value={value.id}
            onChange={(e) => onChange({ mode: 'pick', id: e.target.value })}
            aria-invalid={error ? true : undefined}
          >
            <option value="">— Chọn {what} —</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        ))}
      {value.mode === 'new' && (
        <Button type="button" variant="outline" onClick={onCreate} className="gap-1.5">
          <Plus className="size-4" aria-hidden />
          Tạo {what}
        </Button>
      )}
      {value.mode === 'later' && (
        <p className="flex items-start gap-2 rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink" role="status">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
          <span>{laterWarning}</span>
        </p>
      )}
      {error && <span className="block text-xs text-danger" role="alert">{error}</span>}
    </fieldset>
  )
}

/** Câu hậu quả khi chọn Gán sau bảng giá (đã duyệt 29/10/2026). */
export function priceLaterWarning(unitType: 'agency' | 'ctv' | 'shop' | 'invite'): string {
  if (unitType === 'invite') return 'Chưa có bảng giá thì người đăng ký chưa tạo được đơn.'
  if (unitType === 'agency') return 'Chưa có bảng giá thì Đại lý này và cả nhánh dưới chưa tạo được đơn.'
  return `Chưa có bảng giá thì ${unitType === 'ctv' ? 'CTV' : 'Shop'} này chưa tạo được đơn.`
}

export function schemeLaterWarning(invite = false): string {
  return invite ? 'Chưa có bảng chiết khấu thì người đăng ký chưa được tính chiết khấu.' : 'Chưa có bảng chiết khấu thì CTV này chưa được tính chiết khấu.'
}
